
import { MapPin, Wifi, ChevronDown, LogOut, Settings, ToggleLeft } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/components/auth/AuthProvider";

interface AdminUserMenuProps {
  userName: string | null;
  networkStatus: "checking" | "verified" | "failed";
  onNetworkCheck: () => void;
  onSwitchToStaff: () => void;
  onAdminSettings?: () => void;
}

export const AdminUserMenu = ({
  userName,
  networkStatus,
  onNetworkCheck,
  onSwitchToStaff,
  onAdminSettings
}: AdminUserMenuProps) => {
  const { signOut, profile, isAdmin } = useAuth();
  
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="flex items-center space-x-3">
      {/* Status Indicators */}
      <div className="hidden lg:flex items-center space-x-2">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-50">
          <MapPin className="w-4 h-4 text-green-600" />
        </div>
        <div 
          className={`flex items-center justify-center w-8 h-8 rounded-full cursor-pointer ${
            networkStatus === "verified" ? "bg-green-50" : 
            networkStatus === "failed" ? "bg-red-50" : "bg-yellow-50"
          }`}
          onClick={onNetworkCheck}
        >
          <Wifi className={`w-4 h-4 ${
            networkStatus === "verified" ? "text-green-600" : 
            networkStatus === "failed" ? "text-red-600" : "text-yellow-600"
          }`} />
        </div>
      </div>

      {/* User Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center space-x-2 px-3">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
              {userName ? userName.charAt(0).toUpperCase() : profile?.role === 'admin' ? 'A' : 'S'}
            </div>
            <span className="hidden md:block text-sm font-medium">
              {userName || (profile?.role === 'admin' ? 'Admin' : 'Staff')}
            </span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 bg-white shadow-lg z-50">
          <DropdownMenuLabel>
            {profile?.role === 'admin' ? 'Admin Panel' : 'Staff Panel'}
            <div className="text-xs text-muted-foreground font-normal">
              <Badge variant="outline" className={getRoleColor(profile?.role || 'staff')}>
                {profile?.role || 'Unknown'}
              </Badge>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {onAdminSettings && profile?.role === 'admin' && (
            <>
              <DropdownMenuItem onClick={onAdminSettings}>
                <Settings className="w-4 h-4 mr-2" />
                Admin Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          
          <DropdownMenuItem onClick={signOut}>
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
